import { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CalendarClock, Sparkles, Flame, Target } from "lucide-react";

const DAYS = [
  { key: 0, short: "Mon" }, { key: 1, short: "Tue" }, { key: 2, short: "Wed" },
  { key: 3, short: "Thu" }, { key: 4, short: "Fri" }, { key: 5, short: "Sat" }, { key: 6, short: "Sun" },
];

const INTENSITIES = [
  { key: "light",      label: "Light",      hint: "15-20 min • 2-3 days/week" },
  { key: "normal",     label: "Normal",     hint: "25-40 min • 3-5 days/week" },
  { key: "intensive",  label: "Intensive",  hint: "45-90 min • 5-7 days/week" },
  { key: "flexible",   label: "Flexible",   hint: "You decide day by day" },
];

function browserTZ() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; }
  catch { return "UTC"; }
}

export default function StudyPlannerPage() {
  const { user, refreshMe } = useAuth();
  const [planner, setPlanner] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    days: [0, 2, 4],
    preferred_time: "19:00",
    duration_minutes: 30,
    timezone: browserTZ(),
    intensity: "normal",
  });

  useEffect(() => {
    api.get("/me/study-schedule").then((r) => {
      setSchedule(r.data);
      const p = r.data.planner || {};
      if (p.days?.length) setForm({
        days: p.days,
        preferred_time: p.preferred_time || "19:00",
        duration_minutes: p.duration_minutes || 30,
        timezone: p.timezone || browserTZ(),
        intensity: p.intensity || "normal",
      });
      setPlanner(p);
    }).catch(() => {});
  }, []);

  const toggleDay = (d) => setForm((f) => ({
    ...f,
    days: f.days.includes(d) ? f.days.filter((x) => x !== d) : [...f.days, d].sort((a, b) => a - b),
  }));

  const save = async () => {
    if (!form.days.length) { toast.error("Pick at least one study day"); return; }
    setBusy(true);
    try {
      const { data } = await api.put("/me/study-planner", form);
      setPlanner(data.study_planner);
      const s = await api.get("/me/study-schedule");
      setSchedule(s.data);
      toast.success("Study planner saved");
      await refreshMe?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const isConfigured = (planner?.days || []).length > 0;
  const activity = schedule?.week_activity || {};

  return (
    <div data-testid="planner-root" className="space-y-8 animate-fade-up">
      <div>
        <div className="label-mono text-primary">// study planner</div>
        <h1 className="mt-2 font-display text-3xl font-black tracking-tighter sm:text-4xl">Your rhythm.</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Set your study days and Lane will keep you on track with gentle reminders before every session.
        </p>
      </div>

      {/* Weekly schedule visualization */}
      <section className="rounded-2xl border border-border bg-card p-6">
        <div className="mb-4 flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-primary" />
          <h2 className="font-display text-lg font-bold">This week</h2>
          {schedule?.today_is_study_day && (
            <span className="ml-2 rounded-full border border-accent/40 bg-accent/10 px-2 py-0.5 font-mono text-[0.6rem] uppercase tracking-widest text-accent">
              today is a study day
            </span>
          )}
        </div>
        <div className="grid grid-cols-7 gap-2">
          {DAYS.map((d) => {
            const isStudy = (planner?.days || []).includes(d.key);
            const count = activity[d.key] ?? activity[String(d.key)] ?? 0;
            return (
              <div key={d.key} data-testid={`schedule-day-${d.key}`} className={`rounded-xl border p-3 text-center ${isStudy ? "border-primary/40 bg-primary/5" : "border-border"}`}>
                <div className="label-mono">{d.short}</div>
                <div className={`mt-2 text-2xl font-black tracking-tighter ${isStudy ? "text-primary" : "text-muted-foreground"}`}>
                  {count > 0 ? count : "·"}
                </div>
                <div className="mt-1 text-[0.6rem] uppercase tracking-widest text-muted-foreground">
                  {count > 0 ? `${count} lesson${count === 1 ? "" : "s"}` : (isStudy ? "planned" : "off")}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Configure planner */}
      <section className="rounded-2xl border border-border bg-card p-6">
        <div className="mb-4 flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /><h2 className="font-display text-lg font-bold">{isConfigured ? "Update planner" : "Set your planner"}</h2></div>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label>Study days</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {DAYS.map((d) => (
                <button
                  key={d.key}
                  onClick={() => toggleDay(d.key)}
                  data-testid={`planner-day-${d.key}`}
                  className={`rounded-full border px-3 py-1 font-mono text-xs uppercase tracking-widest transition ${form.days.includes(d.key) ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/40"}`}
                >
                  {d.short}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label>Preferred time</Label>
            <Input type="time" value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} data-testid="planner-time-input" className="mt-2" />
          </div>

          <div>
            <Label>Duration</Label>
            <div className="mt-2 grid grid-cols-4 gap-2">
              {[15, 30, 45, 60].map((n) => (
                <button
                  key={n}
                  onClick={() => setForm({ ...form, duration_minutes: n })}
                  data-testid={`planner-duration-${n}`}
                  className={`rounded-lg border py-2 text-xs font-mono tracking-widest transition ${form.duration_minutes === n ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/40"}`}
                >
                  {n} min
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label>Timezone</Label>
            <Input value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} data-testid="planner-tz-input" className="mt-2" placeholder="e.g. Europe/London" />
            <div className="mt-1 label-mono">Auto-detected — edit if you're travelling.</div>
          </div>
        </div>

        <div className="mt-5">
          <Label>Intensity</Label>
          <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
            {INTENSITIES.map((i) => (
              <button
                key={i.key}
                onClick={() => setForm({ ...form, intensity: i.key })}
                data-testid={`planner-intensity-${i.key}`}
                className={`rounded-xl border p-3 text-left transition ${form.intensity === i.key ? "border-primary bg-primary/10" : "border-border hover:border-primary/40"}`}
              >
                <div className="font-display font-bold">{i.label}</div>
                <div className="mt-1 text-xs text-muted-foreground">{i.hint}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between gap-3">
          <div className="label-mono">
            {user?.learning_goal?.target ? <>Tied to your goal: <span className="text-primary">{user.learning_goal.target}</span></> : "Set a goal first to get personalised reminders."}
          </div>
          <Button
            data-testid="planner-save-btn"
            onClick={save}
            disabled={busy || !form.days.length}
            className="rounded-full btn-neon font-mono text-xs uppercase tracking-widest"
          >
            Save planner
          </Button>
        </div>
      </section>

      {/* Info card */}
      <section className="rounded-2xl border border-primary/30 bg-primary/5 p-6">
        <div className="flex items-start gap-3">
          <div className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-full border border-primary/40 bg-primary/10 text-primary">
            <Flame className="h-4 w-4" />
          </div>
          <div>
            <div className="font-display font-bold">How reminders work</div>
            <ul className="mt-2 space-y-1 text-sm text-muted-foreground list-disc pl-4">
              <li>Lane sends a gentle email <strong>~20 minutes before</strong> every scheduled session.</li>
              <li>If you skip today's session, you get a warm "still time" nudge later that evening.</li>
              <li>Every Sunday you get a personal learning review from your mentor with next-week focus.</li>
              <li>Off days = zero emails. Turn everything off from <strong>Settings → Notifications</strong>.</li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
